import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';

function PaymentUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [payment, setPayment] = useState(null);
  const [pix, setPix] = useState(null);
  const [pixError, setPixError] = useState(null);
  const [pixLoading, setPixLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        const response = await apiClient.get('/payments/my');
        setPayment(response.data.payment);
      } catch (err) {
        // No payment found is not an error
      }
    };

    const fetchPix = async () => {
      try {
        setPixLoading(true);
        const response = await apiClient.get('/payments/pix-qrcode');
        setPix(response.data);
        setPixError(null);
      } catch (err) {
        setPixError(
          err.response?.data?.error ||
          'Não foi possível carregar o QR Code PIX. Tente novamente mais tarde.'
        );
      } finally {
        setPixLoading(false);
      }
    };

    if (user) {
      fetchPayment();
      fetchPix();
    }
  }, [user]);

  const confirmCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      if (document.execCommand('copy')) confirmCopied();
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleCopyPayload = () => {
    if (!pix?.payload) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(pix.payload)
        .then(confirmCopied)
        .catch(() => fallbackCopy(pix.payload));
    } else {
      fallbackCopy(pix.payload);
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Selecione um arquivo para upload');
      return;
    }

    const formData = new FormData();
    formData.append('comprovante', file);

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const response = await apiClient.post('/payments/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setPayment(response.data.payment);
      setSuccess('Comprovante enviado com sucesso! Aguarde a verificação.');
      setFile(null);
      document.getElementById('file-input').value = '';
    } catch (err) {
      setError(
        err.response?.data?.error ||
        'Erro ao fazer upload do comprovante'
      );
      console.error('Error uploading comprovante:', err);
    } finally {
      setUploading(false);
    }
  };

  if (!user) {
    return <div className="container">Faça login para fazer upload do comprovante</div>;
  }

  return (
    <div className="container">
      <h1 className="text-2xl font-bold mb-6 text-center text-primary">Upload de Comprovante de Pagamento</h1>

      {success && <div className="alert alert-success mb-4">{success}</div>}
      {error && <div className="alert alert-error mb-4">{error}</div>}

      <div className="card mb-6 text-center bg-gradient-to-b from-yellow-100 to-white border-2 border-primary">
        <h3 className="text-xl font-bold mb-4 text-primary">QR Code Pix</h3>

        {pixLoading && (
          <div className="py-8 text-gray-600">Gerando QR Code...</div>
        )}

        {!pixLoading && pixError && (
          <div className="alert alert-error">{pixError}</div>
        )}

        {!pixLoading && pix && (
          <>
            <div className="inline-block p-4 bg-white border-4 border-primary rounded-lg shadow-md max-w-full">
              <img
                src={pix.qrcodeBase64}
                alt="QR Code PIX para pagamento da inscrição"
                className="w-48 h-48 mx-auto"
              />
            </div>

            <p className="mt-4 text-sm text-blue-800 font-medium">
              Escaneie o QR code acima para fazer o pagamento via Pix
            </p>

            <div className="mt-3 text-2xl font-bold text-primary">
              {formatCurrency(pix.amount)}
            </div>
            <div className="text-sm text-gray-600">
              Beneficiário: {pix.merchantName}
            </div>

            <div className="mt-4">
              <label className="block text-xs text-gray-500 mb-1">
                Ou copie o código PIX (copia e cola):
              </label>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch">
                <input
                  type="text"
                  readOnly
                  value={pix.payload}
                  className="flex-1 min-w-0 px-3 py-2 border rounded text-xs font-mono bg-gray-50"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  onClick={handleCopyPayload}
                  className="btn btn-primary text-sm whitespace-nowrap"
                >
                  {copied ? 'Copiado!' : 'Copiar código'}
                </button>
              </div>
            </div>

            <div className="mt-3 px-4 py-2 bg-green-100 text-primary rounded-full text-xs font-semibold inline-block">
              Pagamento Seguro
            </div>
          </>
        )}
      </div>

      {payment && (
        <div className={`card border-2 mb-6 ${payment.verified ? 'border-green-600 bg-gradient-to-b from-white to-green-50' : (payment.verification_notes ? 'border-red-600 bg-gradient-to-b from-white to-red-50' : 'border-yellow-600 bg-gradient-to-b from-white to-yellow-50')}`}>
          <h3 className={`text-xl font-bold mb-4 flex items-center ${payment.verified ? 'text-primary' : (payment.verification_notes ? 'text-red-800' : 'text-yellow-800')}`}>
            <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812c-.051-.643-.304-1.254-.723-1.745a3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Comprovante Enviado
          </h3>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-1">
              <strong className={payment.verified ? 'text-primary' : (payment.verification_notes ? 'text-red-800' : 'text-yellow-800')}>Status:</strong>
              {payment.verified ? (
                <span className="ml-2 px-3 py-1 bg-green-100 text-primary rounded-full text-sm font-semibold border border-green-200">
                  <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Verificado
                </span>
              ) : payment.verification_notes ? (
                <span className="ml-2 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold border border-red-200">
                  <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Recusado
                </span>
              ) : (
                <span className="ml-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold border border-yellow-200">
                  <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Em análise
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <strong className={payment.verified ? 'text-primary' : (payment.verification_notes ? 'text-red-800' : 'text-yellow-800')}>Comprovante:</strong>
              <a
                href={payment.comprovante_url}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 text-blue-600 hover:text-blue-800 hover:underline flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z" />
                </svg>
                Visualizar/Baixar arquivo
              </a>
            </div>
            {payment.verification_notes && (
              <div className={`p-3 border rounded ${payment.verified ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-start">
                  <svg className={`w-5 h-5 mr-2 mt-0.5 ${payment.verified ? 'text-green-600' : 'text-red-600'}`} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className={payment.verified ? 'text-primary' : 'text-red-800'}>Observações:</strong>
                    <p className={`mt-1 ${payment.verified ? 'text-green-700' : 'text-red-700'}`}>{payment.verification_notes}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {(!payment || !payment.verified) && (
        <div className="card border-2 border-blue-200 bg-gradient-to-b from-white to-blue-50">
          <h3 className="text-xl font-bold mb-4 text-blue-800 flex items-center">
            <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            {payment ? 'Re-enviar Comprovante' : 'Enviar Comprovante'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-6">
              <div className="form-control">
                <label className="form-label text-primary">Selecionar arquivo (imagem ou PDF)</label>
                <div className="relative">
                  <input
                    type="file"
                    id="file-input"
                    className="form-input w-full cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-yellow-50 file:text-yellow-800 hover:file:bg-yellow-100"
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                  />
                  {file && (
                    <div className="mt-2 text-sm text-green-700 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Arquivo selecionado: {file.name}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-primary w-full flex items-center justify-center focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enviando...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 11-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    Enviar Comprovante
                  </>
                )}
              </button>
              <p className="text-xs text-green-700 mt-2 text-center">
                Envie o comprovante do pagamento via Pix. Arquivos suportados: imagens (JPG, PNG) e PDF.
              </p>
            </form>
          </div>
      )}
    </div>
  );
}

export default PaymentUpload;